import { useQuery } from "@rocicorp/zero/react";
import { queries } from "../../../shared/queries";
import { useCurrentUserGrants } from "../../hooks/useCurrentUserGrants";
import { ActiveWorkflowsBySpace } from "./ActiveWorkflowsBySpace";
import { AutofixAutotunePanel } from "./AutofixAutotunePanel";
import { DuplicateWorkflowsPanel } from "./DuplicateWorkflowsPanel";
import { ExecutionsBySpace } from "./ExecutionsBySpace";
import { FailingWorkflowsPanel } from "./FailingWorkflowsPanel";
import { MonitoringLayout } from "./MonitoringLayout";
import { MonitoringScopeSelector } from "./MonitoringScopeSelector";
import { RouteAuthPanel } from "./RouteAuthPanel";
import { routes } from "../../lib/urls";
import { STORAGE_USAGE_VIEW_FLAG } from "../../../shared/featureFlagNames";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import { useHasAccess } from "../../hooks/useHasAccess";
import { StorageBySpace } from "./StorageBySpace";
import { StorageUsageBySpace } from "./StorageUsageBySpace";
import { TinesUnitsPanel } from "./TinesUnitsPanel";
import { WorkflowDependenciesPanel } from "./WorkflowDependenciesPanel";
import {
  useMonitoringSpaces,
  useMonitoringWorkflows,
} from "./useMonitoringWorkflows";
import {
  SideStack,
  SideStackPair,
  WorkflowsGrid,
  WorkflowsShell,
} from "./workflowsLayout";

// Keep this gate aligned with panel loading conditions, since narrow rows size to content and skeletons cannot reserve the final scroll height.
function useDashboardReady() {
  const { loading } = useMonitoringWorkflows();
  const { loading: spacesLoading } = useMonitoringSpaces();
  const [, grantsResult] = useCurrentUserGrants();
  const [, tenantResult] = useQuery(queries.currentTenant());
  const [, autofixResult] = useQuery(queries.monitorAutofixAutotune());
  const [, pairsResult] = useQuery(queries.monitorDuplicatePairKeys());
  const [, reviewsResult] = useQuery(queries.monitorDuplicateReviews());
  const [, detectionResult] = useQuery(queries.monitorDuplicateDetectionRun());

  return (
    !loading &&
    !spacesLoading &&
    grantsResult.type === "complete" &&
    // RouteAuthPanel renders its final error view when the tenant query fails.
    tenantResult.type !== "unknown" &&
    autofixResult.type === "complete" &&
    pairsResult.type === "complete" &&
    reviewsResult.type === "complete" &&
    detectionResult.type === "complete"
  );
}

export function Workflows() {
  // The units gauge mixes per-space stats with tenant-wide network usage, so
  // it only renders for viewers whose reads cover every space — otherwise the
  // inputs would cover different subsets and the total would be misleading.
  const canReadAllSpaces = useHasAccess("tenant:spaces_read");
  const ready = useDashboardReady();
  const storageUsageView = useFeatureFlag(STORAGE_USAGE_VIEW_FLAG) === true;
  return (
    <MonitoringLayout
      ready={ready}
      headerItem={
        <MonitoringScopeSelector route={routes.monitoring.workflows} />
      }
    >
      <WorkflowsShell>
        {canReadAllSpaces && <TinesUnitsPanel />}
        <WorkflowsGrid>
          <ActiveWorkflowsBySpace />
          <ExecutionsBySpace />
          {storageUsageView ? <StorageUsageBySpace /> : <StorageBySpace />}
          <RouteAuthPanel />
          <SideStack>
            <FailingWorkflowsPanel />
            <DuplicateWorkflowsPanel />
            <SideStackPair>
              <AutofixAutotunePanel />
              <WorkflowDependenciesPanel />
            </SideStackPair>
          </SideStack>
        </WorkflowsGrid>
      </WorkflowsShell>
    </MonitoringLayout>
  );
}
